import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Analytics — loads tracking scripts based on env vars + cookie consent.
 *
 * Supported providers (set any combination of env vars in Railway to enable):
 * - REACT_APP_PLAUSIBLE_DOMAIN: Plausible (cookieless, GDPR-friendly, NO consent needed)
 * - REACT_APP_CLARITY_ID:        Microsoft Clarity (heatmaps + replays, NEEDS consent)
 * - REACT_APP_GA4_ID:            Google Analytics 4 (NEEDS consent)
 * - REACT_APP_META_PIXEL_ID:     Meta Pixel for Facebook/Instagram Ads (NEEDS consent)
 *
 * Plausible is loaded unconditionally because it doesn't use cookies and is
 * GDPR-compliant by design. Other providers wait for explicit user consent
 * stored in localStorage (cookie_analytics=true) before activating.
 */
const Analytics = () => {
  const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
  const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
  const GA4_ID = process.env.REACT_APP_GA4_ID;
  const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

  const [analyticsAllowed, setAnalyticsAllowed] = useState(false);

  useEffect(() => {
    const check = () => {
      const consent = localStorage.getItem('cookie_consent');
      const analyticsFlag = localStorage.getItem('cookie_analytics');
      setAnalyticsAllowed(consent && analyticsFlag === 'true');
    };
    check();
    // Re-check when user updates their consent
    window.addEventListener('storage', check);
    const id = setInterval(check, 2000); // also poll for in-tab updates
    return () => {
      window.removeEventListener('storage', check);
      clearInterval(id);
    };
  }, []);

  return (
    <Helmet>
      {/* Plausible — cookieless, loads regardless of consent */}
      {PLAUSIBLE_DOMAIN && (
        <script
          defer
          data-domain={PLAUSIBLE_DOMAIN}
          src="https://plausible.io/js/script.js"
        />
      )}

      {/* Microsoft Clarity — needs consent */}
      {CLARITY_ID && analyticsAllowed && (
        <script>{`
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${CLARITY_ID}");
        `}</script>
      )}

      {/* Google Analytics 4 — needs consent */}
      {GA4_ID && analyticsAllowed && (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`} />
      )}
      {GA4_ID && analyticsAllowed && (
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA4_ID}', { anonymize_ip: true });
        `}</script>
      )}

      {/* Meta Pixel — needs consent */}
      {META_PIXEL_ID && analyticsAllowed && (
        <script>{`
          !function(f,b,e,v,n,t,s){
            if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
            t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
          }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${META_PIXEL_ID}');
          fbq('track', 'PageView');
        `}</script>
      )}
    </Helmet>
  );
};

export default Analytics;
